/**
 * Cevro Messenger SDK
 *
 * A customer support chat widget for web applications.
 *
 * @example
 * // ES Module
 * import CevroMessenger from '@cevro/messenger-sdk';
 *
 * const messenger = new CevroMessenger({
 *   apiBase: 'https://chat.cevro.ai',
 *   workspaceId: 'your-workspace-id',
 * });
 *
 * messenger.boot();
 *
 * @example
 * // UMD (Script tag)
 * <script src="https://cdn.jsdelivr.net/npm/@cevro/messenger-sdk"></script>
 * <script>
 *   const messenger = new CevroMessenger({
 *     apiBase: 'https://api.yoursite.com',
 *     workspaceId: 'your-workspace-id',
 *   });
 *   messenger.boot();
 * </script>
 */

// Import styles as string for Shadow DOM injection
import styles from './styles/index.css?inline';

// Export styles for Shadow DOM
export { styles as __styles };

// Export main class
export { CevroMessenger } from './core/CevroMessenger';

// Export types
export type {
  CevroMessengerConfig,
  PlayerData,
  SessionData,
  Message,
  Conversation,
  QuickAction,
  RichContent,
} from './types';

// Default export for convenience
import { CevroMessenger } from './core/CevroMessenger';
export default CevroMessenger;

// Global instance management for UMD builds and loader pattern
declare global {
  interface Window {
    CevroMessenger: typeof CevroMessenger;
    cevroMessenger?: CevroMessenger;
    /** Settings object set by partner embed snippet, read by loader.js */
    cevroSettings?: import('./types').CevroMessengerConfig;
    /** Command queue / live proxy created by embed snippet and replaced by loader */
    Cevro?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

// Auto-attach to window for UMD builds
if (typeof window !== 'undefined') {
  window.CevroMessenger = CevroMessenger;
}
